import { Button } from '@/components/ui/button'
import {
  ArrowRight,
  BookOpen,
  Calculator,
  ChevronDown,
  ChevronRight,
  Headphones,
  Layers,
  Menu,
  Network,
  Puzzle,
  TrendingUp,
  User,
  Users,
  Workflow,
  Wrench,
  X
} from 'lucide-react'
import { useState, useEffect, useRef } from 'react'
import Logo from '@/assets/images/logo.png'
import LogoDark from '@/assets/images/logo-dark.png'
import { Link, useNavigate, useLocation } from 'react-router-dom'
import { useSession } from '@/hooks/use-session'

/**
 * Direct navigation links for products shown in the header.
 * These appear as standalone links rather than in a dropdown.
 */
const directProductLinks = [
  {
    name: 'Agent Studio',
    href: '/product/agent-studio'
  },
  {
    name: 'Workflow Builder',
    href: '/product/workflow-builder'
  }
]

/**
 * Product navigation items for the header dropdown.
 * Contains links to product pages with icons and descriptions.
 */
const productItems = [
  {
    name: 'Knowledge Base',
    href: '/product/knowledge-base',
    description: 'Connect your enterprise data',
    icon: BookOpen
  },
  {
    name: 'Integrations',
    href: '/product/integration',
    description: 'Enterprise connections',
    icon: Puzzle
  },
  {
    name: 'Multi-Agent Orchestration',
    href: '/product/multi-agent',
    description: 'Coordinate AI teams',
    icon: Network
  }
]

/**
 * Use case items for the header dropdown.
 * Contains links to detailed use case pages.
 */
const useCaseItems = [
  {
    name: 'ARIS HR Integration',
    href: '/use-cases/aris-hr-integration',
    description: 'Automate ARIS HR operations',
    icon: Users
  },
  {
    name: 'Qatar Gratuity Calculator',
    href: '/use-cases/qatar-gratuity-calculator',
    description: 'End-of-service calculations',
    icon: Calculator
  },
  {
    name: 'AI Recruitment Assistant',
    href: '/use-cases/ai-recruitment-assistant',
    description: 'Screen candidates faster',
    icon: Users
  },
  {
    name: 'Support Ticket Automation',
    href: '/use-cases/support-ticket-automation',
    description: '24/7 ticket handling',
    icon: Headphones
  },
  {
    name: 'Lead Qualification',
    href: '/use-cases/lead-qualification',
    description: 'AI-powered lead scoring',
    icon: TrendingUp
  }
]

/**
 * Main navigation links for the header.
 * Contains links to primary website pages.
 */
const navigationLinks: { name: string; href: string }[] = []

/**
 * Glass morphism header component with modern navigation.
 *
 * Features:
 * - Glass effect with backdrop blur and transparency
 * - Smooth scroll-based background transition
 * - Mobile-friendly slide-out menu with touch-friendly targets
 * - Clean typography with proper hierarchy
 * - Product mega menu with icons and descriptions
 * - Responsive design for all screen sizes
 */
export function Header() {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const [isProductOpen, setIsProductOpen] = useState(false)
  const [isUseCasesOpen, setIsUseCasesOpen] = useState(false)
  const [isMobileProductOpen, setIsMobileProductOpen] = useState(false)
  const [isMobileUseCasesOpen, setIsMobileUseCasesOpen] = useState(false)
  const [isScrolled, setIsScrolled] = useState(false)
  const productMenuRef = useRef<HTMLDivElement>(null)
  const productButtonRef = useRef<HTMLButtonElement>(null)
  const useCaseMenuRef = useRef<HTMLDivElement>(null)
  const useCaseButtonRef = useRef<HTMLButtonElement>(null)
  const navigate = useNavigate()
  const location = useLocation()
  const { isAuthenticated } = useSession()

  // Check if the website is accessed from tmmate.ai origin
  const isTmmateOrigin = typeof window !== 'undefined' && window.location.origin === 'https://tmmate.ai'

  // Determine if current page has a dark hero section
  const hasDarkHero = location.pathname.startsWith('/product/') ||
    location.pathname.startsWith('/use-cases') ||
    location.pathname === '/demo'

  // Use light text when on dark hero pages and not scrolled
  const useLightText = hasDarkHero && !isScrolled

  useEffect(() => {
    // Reset scroll state on route change
    setIsScrolled(false)
  }, [location.pathname])

  useEffect(() => {
    let mainContainer: HTMLElement | null = null

    const handleScroll = () => {
      // Check both window scroll and main container scroll
      if (!mainContainer) {
        mainContainer = document.getElementById('main-scroll-container')
      }
      const scrollY = mainContainer ? mainContainer.scrollTop : window.scrollY
      setIsScrolled(scrollY > 20)
    }

    // Small delay to ensure main-scroll-container is mounted
    const timeoutId = setTimeout(() => {
      mainContainer = document.getElementById('main-scroll-container')

      // Initial check
      handleScroll()

      // Listen for scroll on both window and main container
      window.addEventListener('scroll', handleScroll, { passive: true })
      if (mainContainer) {
        mainContainer.addEventListener('scroll', handleScroll, { passive: true })
      }
    }, 50)

    return () => {
      clearTimeout(timeoutId)
      window.removeEventListener('scroll', handleScroll)
      if (mainContainer) {
        mainContainer.removeEventListener('scroll', handleScroll)
      }
    }
  }, [])

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        productMenuRef.current &&
        productButtonRef.current &&
        !productMenuRef.current.contains(event.target as Node) &&
        !productButtonRef.current.contains(event.target as Node)
      ) {
        setIsProductOpen(false)
      }
      if (
        useCaseMenuRef.current &&
        useCaseButtonRef.current &&
        !useCaseMenuRef.current.contains(event.target as Node) &&
        !useCaseButtonRef.current.contains(event.target as Node)
      ) {
        setIsUseCasesOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = ''
    }
    return () => {
      document.body.style.overflow = ''
    }
  }, [isMobileMenuOpen])

  const handleNavClick = (href: string) => {
    setIsMobileMenuOpen(false)
    setIsProductOpen(false)
    setIsUseCasesOpen(false)
    setIsMobileProductOpen(false)
    setIsMobileUseCasesOpen(false)
    navigate(href)
  }

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen)
    setIsMobileProductOpen(false)
    setIsMobileUseCasesOpen(false)
  }

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ease-out ${
          isScrolled
            ? 'bg-white/70 dark:bg-neutral-900/70 backdrop-blur-xl border-b border-white/20 dark:border-neutral-800/50 shadow-lg shadow-neutral-900/5'
            : 'bg-transparent'
        }`}
        style={{ paddingTop: "env(safe-area-inset-top)" }}
      >
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex h-16 lg:h-18 items-center justify-between">
            {/* Logo */}
            <Link
              to="/"
              className="flex items-center gap-2 hover:opacity-80 transition-opacity"
              aria-label="Taqat AI - Home"
            >
              {/*
                When the navbar background is dark (OS dark mode OR on a dark-hero
                page in light mode), show the icon-only LogoDark — matches the
                sign-in screen's dark-mode treatment so the icon stays clearly
                visible without trying to invert dark text on dark background.
              */}
              <img
                src={Logo}
                alt="Taqat AI"
                className={`h-8 w-auto transition-all duration-300 ${
                  useLightText ? 'hidden' : 'dark:hidden'
                }`}
              />
              <img
                src={LogoDark}
                alt="Taqat AI"
                className={`h-8 w-auto transition-all duration-300 ${
                  useLightText ? 'block' : 'hidden dark:block'
                }`}
              />
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center gap-1">
              {/* Direct Product Links */}
              {directProductLinks.map((link) => (
                <Link
                  key={link.name}
                  to={link.href}
                  className={`px-4 py-2.5 text-base font-medium rounded-full transition-all duration-200 ${
                    useLightText
                      ? 'text-white hover:text-primary hover:bg-white/10'
                      : 'text-neutral-700 dark:text-neutral-200 hover:text-primary hover:bg-neutral-100/80 dark:hover:bg-neutral-800/50'
                  }`}
                >
                  {link.name}
                </Link>
              ))}

              {/* Product Dropdown */}
              <div className="relative">
                <button
                  ref={productButtonRef}
                  onClick={() => setIsProductOpen(!isProductOpen)}
                  onMouseEnter={() => {
                    setIsProductOpen(true)
                    setIsUseCasesOpen(false)
                  }}
                  className={`flex items-center gap-1 px-4 py-2.5 text-base font-medium rounded-full transition-all duration-200 ${
                    isProductOpen
                      ? 'text-primary bg-primary/10'
                      : useLightText
                        ? 'text-white hover:text-primary hover:bg-white/10'
                        : 'text-neutral-700 dark:text-neutral-200 hover:text-primary hover:bg-neutral-100/80 dark:hover:bg-neutral-800/50'
                  }`}
                >
                  Product
                  <ChevronDown
                    className={`w-4 h-4 transition-transform duration-200 ${
                      isProductOpen ? 'rotate-180' : ''
                    }`}
                  />
                </button>

                {/* Product Mega Menu */}
                {isProductOpen && (
                  <div
                    ref={productMenuRef}
                    onMouseLeave={() => setIsProductOpen(false)}
                    className="absolute top-full left-0 mt-2 w-80 origin-top-left animate-in fade-in-0 zoom-in-95 duration-200"
                  >
                    <div className="bg-white/80 dark:bg-neutral-900/80 backdrop-blur-xl rounded-2xl border border-neutral-200/50 dark:border-neutral-700/50 shadow-2xl shadow-neutral-900/10 overflow-hidden">
                      <div className="p-2">
                        {productItems.map((item, index) => {
                          const Icon = item.icon
                          return (
                            <button
                              key={item.name}
                              onClick={() => handleNavClick(item.href)}
                              className={`w-full flex items-center gap-3 px-3 py-3 rounded-xl text-left transition-all duration-200 group hover:bg-primary/10 ${
                                index === 0 ? 'bg-gradient-to-r from-primary/5 to-transparent' : ''
                              }`}
                            >
                              <div
                                className={`w-10 h-10 rounded-xl flex items-center justify-center transition-all duration-200 ${
                                  index === 0
                                    ? 'bg-primary text-white'
                                    : 'bg-neutral-100 dark:bg-neutral-800 text-neutral-600 dark:text-neutral-400 group-hover:bg-primary group-hover:text-white'
                                }`}
                              >
                                <Icon className="w-5 h-5" />
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="text-base font-semibold text-neutral-900 dark:text-white group-hover:text-primary transition-colors">
                                  {item.name}
                                </div>
                                <div className="text-sm text-neutral-500 dark:text-neutral-400 truncate">
                                  {item.description}
                                </div>
                              </div>
                              <ChevronRight className="w-4 h-4 text-neutral-400 opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-200" />
                            </button>
                          )
                        })}
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Use Cases Dropdown */}
              <div className="relative">
                <button
                  ref={useCaseButtonRef}
                  onClick={() => setIsUseCasesOpen(!isUseCasesOpen)}
                  onMouseEnter={() => {
                    setIsUseCasesOpen(true)
                    setIsProductOpen(false)
                  }}
                  className={`flex items-center gap-1 px-4 py-2.5 text-base font-medium rounded-full transition-all duration-200 ${
                    isUseCasesOpen
                      ? 'text-primary bg-primary/10'
                      : useLightText
                        ? 'text-white hover:text-primary hover:bg-white/10'
                        : 'text-neutral-700 dark:text-neutral-200 hover:text-primary hover:bg-neutral-100/80 dark:hover:bg-neutral-800/50'
                  }`}
                >
                  Use Cases
                  <ChevronDown
                    className={`w-4 h-4 transition-transform duration-200 ${
                      isUseCasesOpen ? 'rotate-180' : ''
                    }`}
                  />
                </button>

                {/* Use Cases Mega Menu */}
                {isUseCasesOpen && (
                  <div
                    ref={useCaseMenuRef}
                    onMouseLeave={() => setIsUseCasesOpen(false)}
                    className="absolute top-full left-0 mt-2 w-80 origin-top-left animate-in fade-in-0 zoom-in-95 duration-200"
                  >
                    <div className="bg-white/80 dark:bg-neutral-900/80 backdrop-blur-xl rounded-2xl border border-neutral-200/50 dark:border-neutral-700/50 shadow-2xl shadow-neutral-900/10 overflow-hidden">
                      <div className="p-2">
                        {useCaseItems.map((item, index) => {
                          const Icon = item.icon
                          return (
                            <button
                              key={item.name}
                              onClick={() => handleNavClick(item.href)}
                              className={`w-full flex items-center gap-3 px-3 py-3 rounded-xl text-left transition-all duration-200 group hover:bg-primary/10 ${
                                index === 0 ? 'bg-gradient-to-r from-primary/5 to-transparent' : ''
                              }`}
                            >
                              <div
                                className={`w-10 h-10 rounded-xl flex items-center justify-center transition-all duration-200 ${
                                  index === 0
                                    ? 'bg-primary text-white'
                                    : 'bg-neutral-100 dark:bg-neutral-800 text-neutral-600 dark:text-neutral-400 group-hover:bg-primary group-hover:text-white'
                                }`}
                              >
                                <Icon className="w-5 h-5" />
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="text-base font-semibold text-neutral-900 dark:text-white group-hover:text-primary transition-colors">
                                  {item.name}
                                </div>
                                <div className="text-sm text-neutral-500 dark:text-neutral-400 truncate">
                                  {item.description}
                                </div>
                              </div>
                              <ChevronRight className="w-4 h-4 text-neutral-400 opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-200" />
                            </button>
                          )
                        })}
                        {/* View All Link */}
                        <button
                          onClick={() => handleNavClick('/use-cases')}
                          className="w-full flex items-center justify-center gap-2 px-3 py-2.5 mt-1 rounded-xl text-primary text-sm font-medium hover:bg-primary/10 transition-all duration-200 border-t border-neutral-100 dark:border-neutral-800"
                        >
                          View All Use Cases
                          <ChevronRight className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Navigation Links */}
              {navigationLinks.map((link) => (
                <Link
                  key={link.name}
                  to={link.href}
                  className="px-4 py-2.5 text-base font-medium text-neutral-700 dark:text-neutral-200 hover:text-primary hover:bg-neutral-100/80 dark:hover:bg-neutral-800/50 rounded-full transition-all duration-200"
                >
                  {link.name}
                </Link>
              ))}
            </nav>

            {/* Desktop CTAs */}
            <div className="hidden lg:flex items-center gap-3">
              {isAuthenticated ? (
                <Button
                  onClick={() => navigate('/app')}
                  className="bg-primary hover:bg-primary-600 text-white rounded-full px-6 h-10 text-base font-medium shadow-lg shadow-primary/25 hover:shadow-xl hover:shadow-primary/30 transition-all duration-300"
                >
                  <User className="w-4 h-4 mr-2" />
                  Dashboard
                </Button>
              ) : (
                <>
                  {!isTmmateOrigin && (
                    <Button
                      variant="ghost"
                      onClick={() => navigate('/signin')}
                      className={`rounded-full px-5 h-10 text-base font-medium transition-all duration-200 ${
                        useLightText
                          ? 'text-white hover:text-primary hover:bg-white/10'
                          : 'text-neutral-700 dark:text-neutral-200 hover:text-primary hover:bg-neutral-100/80 dark:hover:bg-neutral-800/50'
                      }`}
                    >
                      Sign In
                    </Button>
                  )}
                  <Button
                    onClick={() => navigate('/demo')}
                    className="bg-primary hover:bg-primary-600 text-white rounded-full px-6 h-10 text-base font-medium shadow-lg shadow-primary/25 hover:shadow-xl hover:shadow-primary/30 hover:-translate-y-0.5 transition-all duration-300"
                  >
                    Book a Demo
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </>
              )}
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={toggleMobileMenu}
              className={`lg:hidden flex items-center justify-center w-10 h-10 rounded-full transition-colors ${
                useLightText
                  ? 'bg-white/10 text-white hover:bg-white/20'
                  : 'bg-neutral-100/80 dark:bg-neutral-800/80 text-neutral-700 dark:text-neutral-200 hover:bg-neutral-200 dark:hover:bg-neutral-700'
              }`}
              aria-label={isMobileMenuOpen ? 'Close menu' : 'Open menu'}
              aria-expanded={isMobileMenuOpen}
            >
              {isMobileMenuOpen ? (
                <X className="w-5 h-5" />
              ) : (
                <Menu className="w-5 h-5" />
              )}
            </button>
          </div>
        </div>
      </header>

      {/* Mobile Menu Overlay */}
      <div
        className={`fixed inset-0 z-40 lg:hidden transition-all duration-300 ${
          isMobileMenuOpen
            ? 'opacity-100 pointer-events-auto'
            : 'opacity-0 pointer-events-none'
        }`}
      >
        {/* Backdrop */}
        <div
          className="absolute inset-0 bg-neutral-900/20 backdrop-blur-sm"
          onClick={() => setIsMobileMenuOpen(false)}
        />

        {/* Menu Panel */}
        <div
          className={`absolute top-0 right-0 h-full w-full max-w-sm bg-white/95 dark:bg-neutral-900/95 backdrop-blur-xl shadow-2xl transition-transform duration-300 ease-out ${
            isMobileMenuOpen ? 'translate-x-0' : 'translate-x-full'
          }`}
          style={{
            paddingTop: "env(safe-area-inset-top)",
            paddingBottom: "env(safe-area-inset-bottom)",
          }}
        >
          {/* Mobile Menu Header */}
          <div className="flex items-center justify-between h-16 px-6 border-b border-neutral-200/50 dark:border-neutral-800/50">
            <Link
              to="/"
              onClick={() => setIsMobileMenuOpen(false)}
              className="flex items-center"
            >
              <img src={Logo} alt="Taqat AI" className="h-7 w-auto block dark:hidden" />
              <img src={LogoDark} alt="Taqat AI" className="h-7 w-auto hidden dark:block" />
            </Link>
            <button
              onClick={() => setIsMobileMenuOpen(false)}
              className="flex items-center justify-center w-10 h-10 rounded-full bg-neutral-100 dark:bg-neutral-800 text-neutral-600 dark:text-neutral-300 hover:bg-neutral-200 dark:hover:bg-neutral-700 transition-colors"
              aria-label="Close menu"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Mobile Menu Content */}
          <div className="flex flex-col h-[calc(100%-4rem)] overflow-y-auto">
            <nav className="flex-1 px-4 py-6 space-y-2">
              {/* Direct Product Links */}
              {directProductLinks.map((link) => (
                <button
                  key={link.name}
                  onClick={() => handleNavClick(link.href)}
                  className="w-full flex items-center gap-3 px-4 py-4 rounded-xl text-left bg-neutral-50 dark:bg-neutral-800/50 hover:bg-neutral-100 dark:hover:bg-neutral-800 transition-colors active:scale-[0.98]"
                >
                  <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                    <Wrench className="w-5 h-5 text-primary" />
                  </div>
                  <span className="text-base font-semibold text-neutral-900 dark:text-white">
                    {link.name}
                  </span>
                </button>
              ))}

              {/* Product Accordion */}
              <div className="rounded-xl overflow-hidden">
                <button
                  onClick={() => setIsMobileProductOpen(!isMobileProductOpen)}
                  className="w-full flex items-center justify-between px-4 py-4 text-left bg-neutral-50 dark:bg-neutral-800/50 rounded-xl hover:bg-neutral-100 dark:hover:bg-neutral-800 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                      <Layers className="w-5 h-5 text-primary" />
                    </div>
                    <span className="text-base font-semibold text-neutral-900 dark:text-white">
                      Product
                    </span>
                  </div>
                  <ChevronDown
                    className={`w-5 h-5 text-neutral-500 transition-transform duration-200 ${
                      isMobileProductOpen ? 'rotate-180' : ''
                    }`}
                  />
                </button>

                {/* Product Sub-items */}
                <div
                  className={`overflow-hidden transition-all duration-300 ${
                    isMobileProductOpen ? 'max-h-[500px] mt-2' : 'max-h-0'
                  }`}
                >
                  <div className="space-y-1 pl-4">
                    {productItems.map((item) => {
                      const Icon = item.icon
                      return (
                        <button
                          key={item.name}
                          onClick={() => handleNavClick(item.href)}
                          className="w-full flex items-center gap-3 px-4 py-3.5 rounded-xl text-left hover:bg-neutral-100 dark:hover:bg-neutral-800 transition-colors active:scale-[0.98]"
                        >
                          <div className="w-9 h-9 rounded-lg bg-neutral-100 dark:bg-neutral-800 flex items-center justify-center">
                            <Icon className="w-4 h-4 text-neutral-600 dark:text-neutral-400" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="text-base font-medium text-neutral-800 dark:text-neutral-200">
                              {item.name}
                            </div>
                            <div className="text-sm text-neutral-500 dark:text-neutral-400 truncate">
                              {item.description}
                            </div>
                          </div>
                        </button>
                      )
                    })}
                  </div>
                </div>
              </div>

              {/* Use Cases Accordion */}
              <div className="rounded-xl overflow-hidden">
                <button
                  onClick={() => setIsMobileUseCasesOpen(!isMobileUseCasesOpen)}
                  className="w-full flex items-center justify-between px-4 py-4 text-left bg-neutral-50 dark:bg-neutral-800/50 rounded-xl hover:bg-neutral-100 dark:hover:bg-neutral-800 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-secondary/10 flex items-center justify-center">
                      <TrendingUp className="w-5 h-5 text-secondary" />
                    </div>
                    <span className="text-base font-semibold text-neutral-900 dark:text-white">
                      Use Cases
                    </span>
                  </div>
                  <ChevronDown
                    className={`w-5 h-5 text-neutral-500 transition-transform duration-200 ${
                      isMobileUseCasesOpen ? 'rotate-180' : ''
                    }`}
                  />
                </button>

                {/* Use Cases Sub-items */}
                <div
                  className={`overflow-hidden transition-all duration-300 ${
                    isMobileUseCasesOpen ? 'max-h-[600px] mt-2' : 'max-h-0'
                  }`}
                >
                  <div className="space-y-1 pl-4">
                    {useCaseItems.map((item) => {
                      const Icon = item.icon
                      return (
                        <button
                          key={item.name}
                          onClick={() => handleNavClick(item.href)}
                          className="w-full flex items-center gap-3 px-4 py-3.5 rounded-xl text-left hover:bg-neutral-100 dark:hover:bg-neutral-800 transition-colors active:scale-[0.98]"
                        >
                          <div className="w-9 h-9 rounded-lg bg-neutral-100 dark:bg-neutral-800 flex items-center justify-center">
                            <Icon className="w-4 h-4 text-neutral-600 dark:text-neutral-400" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="text-base font-medium text-neutral-800 dark:text-neutral-200">
                              {item.name}
                            </div>
                            <div className="text-sm text-neutral-500 dark:text-neutral-400 truncate">
                              {item.description}
                            </div>
                          </div>
                        </button>
                      )
                    })}
                    {/* View All Link */}
                    <button
                      onClick={() => handleNavClick('/use-cases')}
                      className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl text-primary text-sm font-medium hover:bg-primary/10 transition-colors"
                    >
                      View All Use Cases
                      <ChevronRight className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>

              {/* Navigation Links */}
              {navigationLinks.map((link) => (
                <button
                  key={link.name}
                  onClick={() => handleNavClick(link.href)}
                  className="w-full flex items-center gap-3 px-4 py-4 rounded-xl text-left hover:bg-neutral-100 dark:hover:bg-neutral-800 transition-colors active:scale-[0.98]"
                >
                  <span className="text-base font-medium text-neutral-800 dark:text-neutral-200">
                    {link.name}
                  </span>
                </button>
              ))}

              {/* Divider */}
              <div className="h-px bg-neutral-200 dark:bg-neutral-800 my-4" />

              {/* Secondary Links */}
              <button
                onClick={() => handleNavClick('/contact')}
                className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-left hover:bg-neutral-100 dark:hover:bg-neutral-800 transition-colors active:scale-[0.98]"
              >
                <span className="text-base font-medium text-neutral-600 dark:text-neutral-400">
                  Contact
                </span>
              </button>
              <button
                onClick={() => handleNavClick('/about')}
                className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-left hover:bg-neutral-100 dark:hover:bg-neutral-800 transition-colors active:scale-[0.98]"
              >
                <span className="text-base font-medium text-neutral-600 dark:text-neutral-400">
                  About
                </span>
              </button>
            </nav>

            {/* Mobile CTAs */}
            <div className="p-4 border-t border-neutral-200/50 dark:border-neutral-800/50 space-y-3">
              {isAuthenticated ? (
                <Button
                  onClick={() => {
                    setIsMobileMenuOpen(false)
                    navigate('/app')
                  }}
                  className="w-full bg-primary hover:bg-primary-600 text-white rounded-xl h-12 text-base font-medium shadow-lg shadow-primary/25"
                >
                  <User className="w-5 h-5 mr-2" />
                  Dashboard
                </Button>
              ) : (
                <>
                  <Button
                    onClick={() => {
                      setIsMobileMenuOpen(false)
                      navigate('/demo')
                    }}
                    className="w-full bg-primary hover:bg-primary-600 text-white rounded-xl h-12 text-base font-medium shadow-lg shadow-primary/25"
                  >
                    Book a Demo
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                  {!isTmmateOrigin && (
                    <Button
                      variant="outline"
                      onClick={() => {
                        setIsMobileMenuOpen(false)
                        navigate('/signin')
                      }}
                      className="w-full border-neutral-300 dark:border-neutral-700 text-neutral-700 dark:text-neutral-200 hover:bg-neutral-100 dark:hover:bg-neutral-800 rounded-xl h-12 text-base font-medium"
                    >
                      Sign In
                    </Button>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Spacer for fixed header - hidden on pages with dark hero sections */}
      {!hasDarkHero && <div className="h-16 lg:h-18" />}
    </>
  )
}

Header.displayName = 'Header'
